import * as React from 'react';
import {
    Dialog,
    DialogFooter,
    DialogType,
    DefaultButton,
    PrimaryButton,
    SpinButton,
    Checkbox,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    IModalProps,
    Position,
    mergeStyles,
} from '@fluentui/react';
import { LanguageContext } from '../utilities/LocalizationModule';

interface ChangeQuantityDialogProps {
    isOpen: boolean;
    currentQuantity: number;
    /**
     * Number of users currently assigned to this subscription. Used by the dialog to
     * tell the admin exactly how many users will be auto-revoked on a shrink, so the
     * symmetric-with-Stripe revoke behaviour isn't a silent surprise.
     */
    assignedCount: number;
    /**
     * Optional preview fetcher. Returns the prorated cost preview for a candidate
     * (quantity, prorationBehavior) pair, or null when the preview is unavailable.
     * Dialog debounces calls; passing undefined disables the preview UI entirely.
     */
    fetchPreview?: (quantity: number, prorationBehavior: "create_prorations" | "none") => Promise<{
        totalAmount: number;
        prorationDelta: number;
        currency: string;
        prorationBehavior: "create_prorations" | "none";
    } | null>;
    onConfirm: (newQuantity: number, prorate: boolean) => Promise<void>;
    onDismiss: () => void;
}

type PreviewState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; totalAmount: number; prorationDelta: number; currency: string }
    | { kind: 'unavailable' };

const formatCurrency = (amountMinor: number, currency: string): string => {
    // Stripe returns ISO 4217 codes in lower-case (e.g. "eur"). Intl needs upper-case.
    const code = (currency ?? 'eur').toUpperCase();
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: code,
            currencyDisplay: 'symbol',
        }).format(amountMinor / 100);
    } catch {
        // Fall back to a plain number if the currency code is unknown to Intl.
        return `${(amountMinor / 100).toFixed(2)} ${code}`;
    }
};

const dialogWidth = 560;
const dialogBodyClassName = mergeStyles({
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minWidth: 0,
});
const helperTextClassName = mergeStyles({
    fontSize: 12,
    color: '#605e5c',
    overflowWrap: 'break-word',
});
const impactSlotClassName = mergeStyles({
    minHeight: 48,
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    minWidth: 0,
    selectors: {
        '.ms-MessageBar': {
            width: '100%',
            boxSizing: 'border-box',
        },
    },
});
const previewSlotClassName = mergeStyles({
    minHeight: 56,
    fontSize: 12,
    color: '#605e5c',
    borderTop: '1px solid #edebe9',
    paddingTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
    overflowWrap: 'break-word',
});

const ChangeQuantityDialog: React.FC<ChangeQuantityDialogProps> = ({
    isOpen,
    currentQuantity,
    assignedCount,
    fetchPreview,
    onConfirm,
    onDismiss,
}) => {
    const languageStrings = React.useContext(LanguageContext);

    const [quantity, setQuantity] = React.useState<number>(currentQuantity);
    const [prorate, setProrate] = React.useState<boolean>(true);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const [submitError, setSubmitError] = React.useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [preview, setPreview] = React.useState<PreviewState>({ kind: 'idle' });

    React.useEffect(() => {
        if (isOpen) {
            setQuantity(currentQuantity);
            setProrate(true);
            setValidationError(null);
            setSubmitError(null);
            setIsSubmitting(false);
            setSubmitted(false);
            setPreview({ kind: 'idle' });
        }
    }, [isOpen, currentQuantity]);

    // Debounced preview fetch: every time (quantity, prorate) changes, schedule a
    // ~500ms-delayed fetch. Each new keystroke or toggle cancels the prior pending
    // fetch and starts a fresh one. A request-id guard discards stale responses if
    // the user changes inputs again while a fetch is in flight.
    const previewRequestIdRef = React.useRef<number>(0);
    React.useEffect(() => {
        if (!isOpen || submitted || !fetchPreview) return;
        if (!Number.isInteger(quantity) || quantity < 1) {
            setPreview({ kind: 'idle' });
            return;
        }
        const requestedBehavior: "create_prorations" | "none" = prorate ? "create_prorations" : "none";
        const myRequestId = ++previewRequestIdRef.current;
        let cancelled = false;
        setPreview({ kind: 'loading' });

        const loadPreview = async () => {
            try {
                const result = await fetchPreview(quantity, requestedBehavior);
                if (cancelled || myRequestId !== previewRequestIdRef.current) return; // stale
                if (!result) {
                    setPreview({ kind: 'unavailable' });
                    return;
                }
                setPreview({
                    kind: 'ready',
                    totalAmount: result.totalAmount,
                    prorationDelta: result.prorationDelta,
                    currency: result.currency,
                });
            } catch {
                if (cancelled || myRequestId !== previewRequestIdRef.current) return;
                setPreview({ kind: 'unavailable' });
            }
        };

        if (quantity === currentQuantity) {
            void loadPreview();
            return () => { cancelled = true; };
        }

        const timer = setTimeout(loadPreview, 500);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [isOpen, submitted, quantity, prorate, currentQuantity, fetchPreview]);

    const dialogContentProps = React.useMemo(() => ({
        type: DialogType.normal,
        title: languageStrings.ChangeQuantity,
        closeButtonAriaLabel: languageStrings.Close,
        subText: submitted
            ? languageStrings.QuantityChangeSubmitted
            : languageStrings.ChangeQuantityDescription.replace('{current}', currentQuantity.toString()),
    }), [languageStrings, currentQuantity, submitted]);

    // Describe the projector behaviour that will run when Stripe fires
    // customer.subscription.updated. Mirrors RevokeLeastRecentlyActiveAsync /
    // MigrateMostRecentlyActiveAsync semantics on the backend.
    const impactInfo = React.useMemo<{ kind: 'current' | 'revoke' | 'noRevoke' | 'assign'; message: string } | null>(() => {
        if (!Number.isInteger(quantity) || quantity < 1) return null;
        if (quantity === currentQuantity) {
            return { kind: 'current', message: languageStrings.QuantityNoChangeMessage };
        }
        if (quantity < currentQuantity) {
            const revokeCount = Math.max(0, assignedCount - quantity);
            if (revokeCount > 0) {
                return {
                    kind: 'revoke',
                    message: languageStrings.AutoRevokeMessage.replace('{count}', revokeCount.toString()),
                };
            }
            return { kind: 'noRevoke', message: languageStrings.NoUsersRevokedMessage };
        }
        return {
            kind: 'assign',
            message: languageStrings.AutoAssignMessage.replace('{count}', (quantity - currentQuantity).toString()),
        };
    }, [quantity, currentQuantity, assignedCount, languageStrings]);

    const modalProps: IModalProps = React.useMemo(() => ({
        isBlocking: true,
        overlay: {
            className: mergeStyles({ transition: 'all ease 1s' }),
        },
        topOffsetFixed: true,
    }), []);

    // Confirm is meaningful only when the quantity is a positive integer AND differs
    // from the current value. Without the "differs" guard, clicking Confirm on a freshly
    // opened dialog fires a Stripe update to the same value the row already holds.
    const isQuantityValid = Number.isInteger(quantity) && quantity >= 1;
    const hasQuantityChanged = quantity !== currentQuantity;
    const canConfirm = isQuantityValid && hasQuantityChanged && !isSubmitting;

    const handleConfirm = async () => {
        if (!canConfirm) return;
        setValidationError(null);
        setSubmitError(null);
        setIsSubmitting(true);
        try {
            await onConfirm(quantity, prorate);
            setSubmitted(true);
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message ?? err?.response?.data;
            const fallback = typeof err?.message === 'string' ? err.message : null;
            setSubmitError(
                (typeof apiMsg === 'string' && apiMsg) || fallback || languageStrings.UnexpectedError
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={() => { if (!isSubmitting) onDismiss(); }}
            dialogContentProps={dialogContentProps}
            modalProps={modalProps}
            minWidth={dialogWidth}
            maxWidth={dialogWidth}
        >
            {!submitted && (
                <div className={dialogBodyClassName}>
                    <SpinButton
                        label={languageStrings.NewSeatCount}
                        labelPosition={Position.top}
                        min={1}
                        step={1}
                        value={quantity.toString()}
                        onChange={(_e, newValue) => {
                            const parsed = parseInt(newValue ?? '', 10);
                            // Ignore non-numeric input rather than collapsing to 0 — keeps the
                            // displayed value sane and lets the user correct a typo without
                            // first dealing with a misleading "0".
                            if (!Number.isNaN(parsed)) {
                                setQuantity(parsed);
                            }
                        }}
                        incrementButtonAriaLabel="+1"
                        decrementButtonAriaLabel="-1"
                    />
                    <Checkbox
                        label={languageStrings.ProrateImmediately}
                        checked={prorate}
                        onChange={(_e, checked) => setProrate(!!checked)}
                    />
                    <div className={helperTextClassName}>
                        {languageStrings.ProrateImmediatelyDescription}
                    </div>
                    <div className={helperTextClassName}>
                        {languageStrings.CurrentlyAssignedUsers.replace('{count}', assignedCount.toString())}
                    </div>
                    <div
                        data-testid="quantity-impact-slot"
                        className={impactSlotClassName}
                        aria-live="polite"
                    >
                        {impactInfo && (
                            <MessageBar
                                messageBarType={impactInfo.kind === 'revoke' ? MessageBarType.warning : MessageBarType.info}
                                delayedRender={false}
                            >
                                <span>{impactInfo.message}</span>
                            </MessageBar>
                        )}
                    </div>
                    {fetchPreview && (
                        <div
                            data-testid="quantity-preview-slot"
                            className={previewSlotClassName}
                            aria-live="polite"
                        >
                            {preview.kind === 'loading' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Spinner size={SpinnerSize.xSmall} />
                                    <span>{languageStrings.PreviewLoading}</span>
                                </div>
                            )}
                            {preview.kind === 'unavailable' && (
                                <span>{languageStrings.PreviewUnavailable}</span>
                            )}
                            {preview.kind === 'ready' && (() => {
                                const totalFmt = formatCurrency(preview.totalAmount, preview.currency);
                                const deltaAbsFmt = formatCurrency(Math.abs(preview.prorationDelta), preview.currency);
                                if (quantity === currentQuantity) {
                                    return (
                                        <span><strong>{languageStrings.PreviewCurrentInvoice.replace('{amount}', totalFmt)}</strong></span>
                                    );
                                }
                                let prorationLine: string;
                                if (!prorate) {
                                    prorationLine = languageStrings.PreviewProrationNone;
                                } else if (preview.prorationDelta < 0) {
                                    prorationLine = languageStrings.PreviewProrationCredit.replace('{amount}', deltaAbsFmt);
                                } else {
                                    prorationLine = languageStrings.PreviewProrationDelta.replace('{amount}', deltaAbsFmt);
                                }
                                return (
                                    <>
                                        <span><strong>{prorationLine}</strong></span>
                                        <span>{languageStrings.PreviewUpcomingInvoice.replace('{amount}', totalFmt)}</span>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                    {validationError && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            {validationError}
                        </MessageBar>
                    )}
                    {submitError && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            {submitError}
                        </MessageBar>
                    )}
                </div>
            )}
            {!submitted ? (
                <DialogFooter>
                    <PrimaryButton onClick={handleConfirm} disabled={!canConfirm}>
                        {languageStrings.Confirm}
                        {isSubmitting && <Spinner size={SpinnerSize.small} style={{ marginLeft: 12 }} />}
                    </PrimaryButton>
                    <DefaultButton
                        onClick={() => onDismiss()}
                        disabled={isSubmitting}
                        text={languageStrings.Cancel}
                    />
                </DialogFooter>
            ) : (
                <DialogFooter>
                    <PrimaryButton onClick={() => onDismiss()} text={languageStrings.Ok} />
                </DialogFooter>
            )}
        </Dialog>
    );
};

export default ChangeQuantityDialog;
